import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { trackAddToCart } from '@/lib/metaPixel';

const CartContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Absolute defence-in-depth cap for any single line quantity. Real
// customers never hit this — but a corrupted localStorage row (see the
// pre-fix "quantity 16,558,384" incident) will get clamped here before
// it ever renders. Actual inventory clamping happens below and on the
// backend; this is just the last line of defence for pathological data.
const MAX_SAFE_QTY = 999;
export const MAX_PREORDER_QTY = 5;

const lineCap = (isPre, stock) => (isPre
  ? MAX_PREORDER_QTY
  : (Number.isFinite(stock) && stock > 0 ? Math.min(stock, MAX_SAFE_QTY) : MAX_SAFE_QTY));

/**
 * Coerce a raw cart-item array into safe, valid rows.
 *
 * Any of the following gets fixed rather than allowed to propagate:
 *   • non-integer quantities (NaN, Infinity, "3", 1.5, negatives)
 *   • quantities > inventory_count (for in-stock items) or > MAX_SAFE_QTY
 *   • rows without a product_id
 *   • quantities ≤ 0 → row dropped
 *
 * Called at three critical points:
 *   1. Initial state load from localStorage (post-corruption self-heal).
 *   2. Every `setItems()` write via `commitItems()` below.
 *   3. The `/cart/merge` response before we adopt it into state.
 */
function sanitizeCartItems(rawItems) {
  if (!Array.isArray(rawItems)) return [];
  const cleaned = [];
  const seen = new Set(); // dedupe against duplicate product_ids in the same array
  for (const raw of rawItems) {
    if (!raw || typeof raw !== 'object') continue;
    const pid = raw.product_id;
    if (!pid || seen.has(pid)) continue;
    let q = Number(raw.quantity);
    if (!Number.isFinite(q) || q < 1) q = 1;
    q = Math.max(1, Math.floor(q));
    const stock = Number(raw.inventory_count);
    // In-stock items can never exceed the SKU's inventory. Pre-orders
    // ignore stock (they're reservations against future stock) but
    // still respect the MAX_SAFE_QTY ceiling.
    const isPre = !!raw.is_preorder;
    const cap = lineCap(isPre, stock);
    if (q > cap) q = cap;
    seen.add(pid);
    cleaned.push({ ...raw, quantity: q });
  }
  return cleaned;
}

export function CartProvider({ children }) {
  const [items, setItemsRaw] = useState(() => {
    try {
      return sanitizeCartItems(JSON.parse(localStorage.getItem('ogmini_cart') || '[]'));
    } catch { return []; }
  });
  const [cartBounce, setCartBounce] = useState(false);
  const itemsRef = useRef(items);
  const [lastAdded, setLastAdded] = useState(null);
  const { user } = useAuth();
  // Track the last user_id we reconciled for, so a re-render doesn't
  // fire the merge on every render — only when the user actually
  // changes (guest → signed-in, or account A → account B).
  const lastReconciledUid = useRef(null);
  // Track the previous uid so we can detect an ACTUAL logout (uid
  // transitions present → null) and clear the persisted reconciliation
  // flag then. A page reload while signed-in also passes through
  // `user=null` briefly, but prevUidRef starts null on mount so we
  // never mis-fire a "logout" during that initial hydration.
  const prevUidRef = useRef(null);
  // uid whose server cart is reconciled; local edits are mirrored to it.
  const [syncUid, setSyncUid] = useState(null);

  // Any writer goes through this so corrupt rows never land in state
  // even if a component tried to set them directly.
  const commitItems = useCallback((next) => {
    const raw = typeof next === 'function' ? next(itemsRef.current) : next;
    const sanitized = sanitizeCartItems(raw);
    itemsRef.current = sanitized;
    setItemsRaw(sanitized);
  }, []);

  useEffect(() => {
    localStorage.setItem('ogmini_cart', JSON.stringify(items));
  }, [items]);

  const currentUid = user ? (user._id || user.id || user.email) : null;
  useEffect(() => {
    if (!syncUid || syncUid !== currentUid) return;
    const t = setTimeout(() => {
      const payload = items.map((it) => ({ product_id: it.product_id, quantity: it.quantity }));
      axios.post(`${API}/cart/sync`, { items: payload }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [items, syncUid, currentUid]);

  // ── Guest → signed-in reconciliation ──────────────────────────────
  // When `user` transitions from null to an authenticated identity, take
  // whatever's in the guest cart and merge it into the user's saved cart
  // server-side. Then hydrate the local state with the merged result so
  // both sources of truth agree. If the guest cart was empty we still
  // hydrate from the server so returning users see their prior cart.
  //
  // CRITICAL: this used to run on every page reload because
  // `lastReconciledUid` is in-memory only and resets on remount. Each
  // reload re-added the guest cart's qty to the (already-merged) server
  // cart, doubling the count. After ~24 reloads a qty=1 item ballooned
  // to ~16,558,384. We now also persist a `ogmini_cart_reconciled_for`
  // marker in localStorage keyed by the user's uid, and only clear it
  // on an actual logout (uid transitions present → null). Page reloads
  // read the marker, match uid, and skip the merge.
  useEffect(() => {
    const uid = user ? (user._id || user.id || user.email) : null;

    // Detect an actual logout (uid transitions from set → null). A page
    // reload while signed-in briefly passes through user=null too, but
    // prevUidRef starts as null on mount so that transition is null →
    // null → set, never present → null.
    if (prevUidRef.current && !uid) {
      try { localStorage.removeItem('ogmini_cart_reconciled_for'); } catch (_) { /* private mode */ }
      lastReconciledUid.current = null;
      // Account cart is saved server-side; start the guest session empty so
      // the next sign-in doesn't merge the same items back in.
      commitItems([]);
    }
    prevUidRef.current = uid;

    if (!uid) { setSyncUid(null); return; }

    const persistedFor = (() => {
      try { return localStorage.getItem('ogmini_cart_reconciled_for') || ''; } catch { return ''; }
    })();
    if (lastReconciledUid.current === uid || persistedFor === uid) {
      // Already reconciled this uid in this tab OR in an earlier tab.
      // Skip the merge — otherwise we'd double-count qty on every reload.
      lastReconciledUid.current = uid;
      setSyncUid(uid);
      return;
    }
    lastReconciledUid.current = uid;

    let cancelled = false;
    (async () => {
      try {
        let guestItems = [];
        try { guestItems = JSON.parse(localStorage.getItem('ogmini_cart') || '[]'); } catch { guestItems = []; }
        // Sanitize the guest cart before shipping it to the backend so
        // a corrupted localStorage row can never inflate the server-side
        // quantity. sanitizeCartItems clamps to inventory + MAX_SAFE_QTY
        // and drops invalid rows.
        guestItems = sanitizeCartItems(guestItems);
        const payload = guestItems
          .filter((it) => it && it.product_id && Number(it.quantity) > 0)
          .map((it) => ({ product_id: it.product_id, quantity: Number(it.quantity) }));

        if (payload.length > 0) {
          const { data } = await axios.post(`${API}/cart/merge`, { items: payload });
          if (!cancelled) {
            commitItems(data.items || []);
            const dropped = (data.dropped_product_ids || []).length;
            if (dropped > 0) {
              toast.info(`${dropped} item${dropped > 1 ? 's are' : ' is'} no longer available and were removed from your cart.`);
            } else if (guestItems.length > 0) {
              toast.success('Your cart has been merged with your account.');
            }
          }
        } else {
          // No guest items — just pull the saved cart so it hydrates
          // into the header + cart page.
          const { data } = await axios.get(`${API}/cart`);
          if (!cancelled && Array.isArray(data?.items) && data.items.length > 0) {
            commitItems(data.items);
          }
        }
        // Whether we merged or just fetched, we've now reconciled the
        // guest ↔ account boundary for THIS uid. Persist that so a
        // subsequent tab reload doesn't merge again.
        if (!cancelled) {
          try { localStorage.setItem('ogmini_cart_reconciled_for', uid); } catch (_) { /* private */ }
          setSyncUid(uid);
        }
      } catch (e) {
        // Non-fatal — leave the guest cart untouched so the user can
        // still check out. Diagnostic log only.
        console.error('[Cart] merge failed:', e?.response?.data || e.message);
        // Don't set the reconciled flag — next reload will retry the
        // merge (safe because the guest cart still holds the user's
        // intent and the backend clamps on its side).
      }
    })();
    return () => { cancelled = true; };
  }, [user, commitItems]);

  const addItem = useCallback((product, qty = 1) => {
    // Hard gate: never add a product that's inactive (draft) OR
    // out-of-stock unless it's a pre-order. Prevents customers from
    // sneaking through stale links and protects against the iter25
    // regression where a freshly-deactivated SKU could still be cart-able
    // until the page was refreshed.
    const isPreorder = !!product?.is_preorder;
    const isActive = product?.is_active !== false; // default true if undefined
    const stock = Number(product?.inventory_count ?? 0);
    if (!isActive) {
      toast.error('This product is no longer available.');
      return false;
    }
    if (isPreorder && product?.reservations_full) {
      toast.error(`Reservations are full for ${product?.name || 'this model'}.`);
      return false;
    }
    if (!isPreorder && stock <= 0) {
      toast.error(`${product?.name || 'This product'} is out of stock.`, {
        description: 'You can tap "Notify me" on the product page to get an alert when it returns.',
      });
      return false;
    }

    // Coerce the incoming increment so a caller can't inject NaN /
    // negative / decimal / string values into the cart.
    let add = Number(qty);
    if (!Number.isFinite(add) || add < 1) add = 1;
    add = Math.max(1, Math.floor(add));

    let hitCap = false;
    let actualAdded = 0;
    commitItems(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      const currentQty = existing ? Number(existing.quantity) || 0 : 0;
      const rawNext = currentQty + add;
      // Cap the resulting line to available inventory for in-stock items;
      // pre-orders skip the stock cap since they're reservations against
      // future stock (existing behaviour). MAX_SAFE_QTY still applies.
      const cap = lineCap(isPreorder, stock);
      const nextQty = Math.min(rawNext, cap);
      actualAdded = Math.max(0, nextQty - currentQty);
      if (nextQty === currentQty) {
        hitCap = true;
        return prev; // no-op, already at cap
      }
      if (existing) {
        return prev.map(i => i.product_id === product.id ? { ...i, quantity: nextQty } : i);
      }
      return [...prev, {
        product_id: product.id, name: product.name, price: product.price,
        compare_at_price: product.compare_at_price, image: product.images?.[0] || '',
        brand: product.brand, quantity: nextQty, inventory_count: product.inventory_count,
        // Carry preorder flags so the cart/checkout shipping calc + UI
        // labels can short-circuit without an extra API roundtrip.
        is_preorder: !!product.is_preorder,
        preorder_deposit: product.preorder_deposit || 0,
        expected_arrival_date: product.expected_arrival_date || '',
      }];
    });
    if (hitCap) {
      toast.error('Maximum quantity reached.', {
        description: isPreorder
          ? `Pre-orders are limited to ${MAX_PREORDER_QTY} per customer.`
          : `Only ${stock} in stock right now.`,
      });
      return false;
    }
    // Meta Pixel AddToCart — fired here (the single choke-point every
    // add flows through) so we never miss a click AND never double-fire.
    // Uses the `add` delta, not the resulting total, so Meta attributes
    // the actual customer intent (+1 unit) rather than the cart state.
    trackAddToCart(product, actualAdded);
    // Trigger animation
    setCartBounce(true);
    setLastAdded(product.name);
    setTimeout(() => setCartBounce(false), 800);
    setTimeout(() => setLastAdded(null), 2500);
    return true;
  }, [commitItems]);

  const removeItem = useCallback((productId) => {
    commitItems(prev => prev.filter(i => i.product_id !== productId));
  }, [commitItems]);

  const updateQuantity = useCallback((productId, qty) => {
    // Coerce to a safe integer so the +/- buttons or a bulk-update caller
    // can't push NaN / string / decimal / negative values into state.
    let next = Number(qty);
    if (!Number.isFinite(next)) next = 0;
    next = Math.floor(next);
    if (next <= 0) { removeItem(productId); return; }
    commitItems(prev => prev.map(i => {
      if (i.product_id !== productId) return i;
      const stock = Number(i.inventory_count);
      const isPre = !!i.is_preorder;
      const cap = lineCap(isPre, stock);
      const capped = Math.min(next, cap);
      if (capped < next) {
        toast.error(isPre
          ? `Pre-orders are limited to ${MAX_PREORDER_QTY} per customer.`
          : `Only ${cap} in stock — quantity capped at ${cap}.`);
      }
      return { ...i, quantity: capped };
    }));
  }, [commitItems, removeItem]);

  const clearCart = useCallback(() => {
    commitItems([]);
    localStorage.removeItem('ogmini_cart');
  }, [commitItems]);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, subtotal, itemCount, cartBounce, lastAdded }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
